import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';

const RESEND_COOLDOWN_MS = 60_000;

export function VerificationBanner() {
  const { user, emailVerificationRequired } = useAuth();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [resentAt, setResentAt] = useState<number | null>(null);

  if (!emailVerificationRequired || !user || dismissed) return null;

  const onCooldown = resentAt !== null && Date.now() - resentAt < RESEND_COOLDOWN_MS;

  async function handleResend() {
    if (!user) return;
    try {
      await authService.resendVerification(user.email);
      setResentAt(Date.now());
    } catch {
      /* silent — banner stays available for retry */
    }
  }

  return (
    <div
      className="border-b border-border bg-muted/50 px-4 py-2.5 md:px-6"
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto max-w-7xl flex items-center gap-3 flex-wrap">
        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
        <p className="text-sm text-foreground flex-1 min-w-0">{t('auth.verification_banner')}</p>
        <Button
          size="sm"
          variant="outline"
          onClick={handleResend}
          disabled={onCooldown}
          className="h-8 min-h-11 md:min-h-0"
        >
          {onCooldown ? t('auth.verification_sent') : t('auth.verification_resend_cta')}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', HIT_AREA_44)}
          onClick={() => setDismissed(true)}
          aria-label={t('common.dismiss')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
